import React, { useState, useEffect, useRef } from 'react';
import {
  BadgeCheck,
  Building2,
  Check,
  Eye,
  EyeOff,
  Link2,
  LockKeyhole,
  Pencil,
  Save,
  Trash2,
  UserRound,
  X
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserSectionSidebar from '../components/UserSectionSidebar';
import ImageViewer from '../components/ImageViewer';
import ConfirmModal from '../components/ConfirmModal';
import LoadingIndicator from '../components/LoadingIndicator';
import { useTranslations } from '../translations';
import { compressImage, PROFILE_IMAGE_OPTIONS } from '../utils/imageCompression';
import { isPasswordLengthValid, isPasswordValid, PASSWORD_MAX_LENGTH } from '../utils/passwordValidation';
import { ACCEPTED_IMAGE_INPUT_TYPES, validateImageFile } from '../utils/imageValidation';
import { resolveUploadErrorMessage } from '../utils/uploadError';
import { isValidPhoneNumber, normalizePhoneNumber, sanitizePhoneNumberInput } from '../utils/phoneValidation';
import { isValidSocialMediaUrl } from '../utils/socialMediaUrlValidation';
import { USER_DESCRIPTION_MAX_LENGTH } from '../constants/user';
import {
  deleteAccount,
  deleteUserPicture,
  fetchUserProfile,
  updateUserPicture,
  updateUserProfile
} from '../services/user.api';
import { uploadSingleImage } from '../services/image.api';
import { UserProfile } from '../services/api.types';
import { getAccessToken, endLocalSession, changePassword } from '../services/auth';
import { useNotification } from '../components/NotificationProvider';
import './Profile.css';

interface ProfileProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToAdministration?: () => void;
  onLogout: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
}

const Profile: React.FC<ProfileProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [shouldDeletePicture, setShouldDeletePicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [facebook, setFacebook] = useState('');
  const [instagram, setInstagram] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [facebookError, setFacebookError] = useState('');
  const [instagramError, setInstagramError] = useState('');
  const [tiktokError, setTiktokError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmedNewPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<Set<string>>(new Set());
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [compressingImage, setCompressingImage] = useState(false);

  const hasProfileChanges = Boolean(userProfile && (
    firstName !== (userProfile.firstName ?? '') ||
    lastName !== (userProfile.lastName ?? '') ||
    normalizePhoneNumber(phone) !== normalizePhoneNumber(userProfile.phone ?? '') ||
    description !== (userProfile.description ?? '') ||
    companyName !== (userProfile.companyName ?? '') ||
    facebook !== (userProfile.facebook ?? '') ||
    instagram !== (userProfile.instagram ?? '') ||
    tiktok !== (userProfile.tiktok ?? '') ||
    selectedImage !== null ||
    shouldDeletePicture
  ));

  useEffect(() => {
    const loadUserProfile = async () => {
      if (isAuthChecking) {
        return;
      }
      const token = getAccessToken();
      if (!token) {
        onNavigateToLogin();
        return;
      }

      try {
        const profile = await fetchUserProfile();
        setUserProfile(profile);
        setFirstName(profile.firstName || '');
        setLastName(profile.lastName || '');
        setPhone(profile.phone || '');
        setDescription(profile.description || '');
        setEmail(profile.email || '');
        setCompanyName(profile.companyName || '');
        setFacebook(profile.facebook || '');
        setInstagram(profile.instagram || '');
        setTiktok(profile.tiktok || '');
      } catch (err) {
        console.error('Error fetching user profile:', err);
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        showNotification('Nu am putut încărca profilul. Vă rugăm încercați din nou.', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [onNavigateToLogin, showNotification, isAuthChecking]);

  const handleSave = async () => {
    if (!hasProfileChanges || saving || compressingImage) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      onNavigateToLogin();
      return;
    }

    // Reset errors
    setPhoneError('');
    setFacebookError('');
    setInstagramError('');
    setTiktokError('');

    // Validate contact fields
    let hasError = false;
    if (phone.trim() && !isValidPhoneNumber(phone)) {
      setPhoneError(t.profile.invalidPhone);
      hasError = true;
    }
    if (!isValidSocialMediaUrl(facebook, 'facebook')) {
      setFacebookError(t.profile.invalidFacebookUrl);
      hasError = true;
    }
    if (!isValidSocialMediaUrl(instagram, 'instagram')) {
      setInstagramError(t.profile.invalidInstagramUrl);
      hasError = true;
    }
    if (!isValidSocialMediaUrl(tiktok, 'tiktok')) {
      setTiktokError(t.profile.invalidTiktokUrl);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setSaving(true);

    try {
      if (shouldDeletePicture) {
        await deleteUserPicture();
        setShouldDeletePicture(false);
      }

      if (selectedImage) {
        const uploadResponse = await uploadSingleImage(selectedImage, 'ACCOUNT');
        await updateUserPicture(uploadResponse.imageUrl);
        setSelectedImage(null);
        setImagePreview(null);
      }

      await updateUserProfile({
        firstName,
        lastName,
        phone: normalizePhoneNumber(phone) || '',
        description: description || '',
        companyName: companyName || '',
        termsAndConditions: userProfile?.termsAndConditions || true,
        marketingEmails: userProfile?.marketingEmails || false,
        facebook: facebook || '',
        instagram: instagram || '',
        tiktok: tiktok || ''
      });

      const updatedProfile = await fetchUserProfile();
      setUserProfile(updatedProfile);
      setFirstName(updatedProfile.firstName ?? '');
      setLastName(updatedProfile.lastName ?? '');
      setPhone(updatedProfile.phone ?? '');
      setDescription(updatedProfile.description ?? '');
      setEmail(updatedProfile.email ?? '');
      setCompanyName(updatedProfile.companyName ?? '');
      setFacebook(updatedProfile.facebook ?? '');
      setInstagram(updatedProfile.instagram ?? '');
      setTiktok(updatedProfile.tiktok ?? '');
      showNotification('Cont actualizat cu succes!', 'success');
    } catch (err) {
      console.error('Error updating profile:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      if (errorMessage === 'INVALID_PHONE_NUMBER') {
        setPhoneError(t.profile.invalidPhone);
        return;
      }
      if (errorMessage === 'INVALID_FACEBOOK_URL') {
        setFacebookError(t.profile.invalidFacebookUrl);
        return;
      }
      if (errorMessage === 'INVALID_INSTAGRAM_URL') {
        setInstagramError(t.profile.invalidInstagramUrl);
        return;
      }
      if (errorMessage === 'INVALID_TIKTOK_URL') {
        setTiktokError(t.profile.invalidTiktokUrl);
        return;
      }
      const uploadError = resolveUploadErrorMessage(err, t.upload);
      if (uploadError) {
        showNotification(uploadError, 'error');
        return;
      }
      showNotification('Eroare la actualizarea contului', 'error');
    } finally {
      setSaving(false);
    }
  };

  const hasPhoneVerification = () => {
    return phone.trim().length > 0 && isValidPhoneNumber(phone);
  };

  const displayedPhoneError = phoneError;

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validationError = validateImageFile(file);
      if (validationError) {
        showNotification(t.upload[validationError], 'error');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setCompressingImage(true);
      try {
        // Compress the image before processing
        const compressedFile = await compressImage(file, PROFILE_IMAGE_OPTIONS);
        setSelectedImage(compressedFile);
        setShouldDeletePicture(false);
        const reader = new FileReader();
        reader.onload = (event) => {
          setImagePreview(event.target?.result as string);
          setCompressingImage(false);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error compressing image:', error);
        showNotification(t.upload.processingFailed, 'error');
        setCompressingImage(false);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    if (selectedImage) {
      setSelectedImage(null);
      setImagePreview(null);
    } else if (userProfile?.imageUrl) {
      setShouldDeletePicture(true);
    }
  };

  const handleAddPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteAccountClick = () => {
    setShowDeleteAccountModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteAccountModal(false);
  };

  const handleConfirmDelete = async () => {
    if (deletingAccount) return;

    setDeletingAccount(true);
    try {
      await deleteAccount();
      endLocalSession();
      showNotification(t.profile.deleteAccountSuccess, 'success');
      setShowDeleteAccountModal(false);
      onNavigateToHome();
    } catch (err) {
      console.error('Error deleting account:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification(t.profile.deleteAccountError, 'error');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleChangePasswordClick = () => {
    setShowChangePassword(!showChangePassword);
    if (showChangePassword) {
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmedNewPassword: ''
      });
      setPasswordErrors(new Set());
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    setPasswordErrors(prev => {
      const newErrors = new Set(prev);

      if (name === 'oldPassword' && value.trim()) {
        newErrors.delete('oldPassword');
      }

      if (name === 'newPassword') {
        if (isPasswordValid(value)) {
          newErrors.delete('newPassword');
        }
        if (passwordData.confirmedNewPassword && value === passwordData.confirmedNewPassword) {
          newErrors.delete('confirmedNewPassword');
        }
      }

      if (name === 'confirmedNewPassword' &&
          value === passwordData.newPassword &&
          isPasswordValid(passwordData.newPassword)) {
        newErrors.delete('newPassword');
        newErrors.delete('confirmedNewPassword');
      }

      return newErrors;
    });
  };

  const handleSubmitPasswordChange = async () => {
    const errors = new Set<string>();
    if (!isPasswordLengthValid(passwordData.oldPassword)) errors.add('oldPassword');
    if (!isPasswordValid(passwordData.newPassword)) errors.add('newPassword');
    if (!passwordData.confirmedNewPassword.trim()) errors.add('confirmedNewPassword');

    if (passwordData.newPassword.trim() && passwordData.confirmedNewPassword.trim() &&
        passwordData.newPassword !== passwordData.confirmedNewPassword) {
      errors.add('newPassword');
      errors.add('confirmedNewPassword');
      showNotification(t.profile.passwordMismatch, 'error');
    }

    if (errors.size > 0) {
      setPasswordErrors(errors);
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(passwordData);
      showNotification('Parola a fost schimbată cu succes!', 'success');
      setShowChangePassword(false);
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmedNewPassword: ''
      });
      setPasswordErrors(new Set());
    } catch (err) {
      console.error('Error changing password:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Nu am putut schimba parola. Verificați că ați introdus corect parola actuală.', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={() => {}}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={onNavigateToMessages}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onAdministrationClick={onNavigateToAdministration}
          onSettingsClick={onNavigateToSettings}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
        />
        <div className="profile-container">
          <UserSectionSidebar
            activeItem="profile"
            onLocationsClick={onNavigateToLocations}
            onMessagesClick={() => onNavigateToMessages?.()}
            onAnnouncementsClick={onNavigateToMyAnnouncements}
            onSettingsClick={onNavigateToSettings}
          />
          <main className="profile-main">
            <header className="profile-top-header">
              <div className="profile-page-heading">
                <span className="profile-page-eyebrow">
                  <UserRound size={15} aria-hidden="true" />
                  {t.profile.eyebrow}
                </span>
                <h1 className="profile-title">{t.profile.title}</h1>
                <p>{t.profile.subtitle}</p>
              </div>
            </header>

            <div className="profile-content-section profile-loading-sections">
              <section className="profile-identity-band profile-loading-card">
                <LoadingIndicator message={t.profile.loadingOverview} />
              </section>
              <section className="profile-form-section profile-loading-card profile-loading-details-card">
                <LoadingIndicator message={t.profile.loadingDetails} />
              </section>
            </div>
          </main>
        </div>
        <Footer
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
          onNavigateToCookies={onNavigateToCookies}
          onNavigateToAbout={onNavigateToAbout}
          onNavigateToContact={onNavigateToContact}
        />
      </div>
    );
  }

  const profileInitials = [firstName, lastName]
    .map((name) => name.trim().charAt(0))
    .join('')
    .toUpperCase() || email.trim().charAt(0).toUpperCase() || '?';
  const hasVisibleProfileImage = Boolean(
    imagePreview || (userProfile?.imageUrl && !shouldDeletePicture)
  );
  const completedProfileFields = [
    firstName,
    lastName,
    phone,
    description,
    companyName,
    facebook || instagram || tiktok,
    hasVisibleProfileImage,
  ].filter(Boolean).length;
  const profileCompletion = Math.round((completedProfileFields / 7) * 100);

  return (
    <div className="profile-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={() => {}}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="profile-container">
        <UserSectionSidebar
          activeItem="profile"
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
        />

        <main className="profile-main">
          <header className="profile-top-header">
            <div className="profile-page-heading">
              <span className="profile-page-eyebrow">
                <UserRound size={15} aria-hidden="true" />
                {t.profile.eyebrow}
              </span>
              <h1 className="profile-title">{t.profile.title}</h1>
              <p>{t.profile.subtitle}</p>
            </div>
            <div className="profile-completion" aria-label={t.profile.completionLabel}>
              <span className="profile-completion-icon"><BadgeCheck size={20} aria-hidden="true" /></span>
              <div>
                <strong>{profileCompletion}%</strong>
                <small>{t.profile.completionLabel}</small>
              </div>
            </div>
          </header>

          <div className="profile-content-section">
            <section className="profile-identity-band" aria-label={t.profile.profilePicture}>
              <div className="profile-avatar-section">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_INPUT_TYPES}
                  onChange={handleImageSelect}
                  className="profile-image-file-input"
                />
                <div className="profile-avatar">
                  {imagePreview ? (
                    <button
                      type="button"
                      className="profile-avatar-preview-button"
                      onClick={() => setShowImageViewer(true)}
                      aria-label={t.profile.viewPhoto}
                    >
                      <img src={imagePreview} alt="" className="profile-avatar-image" />
                    </button>
                  ) : (userProfile?.imageUrl && !shouldDeletePicture) ? (
                    <button
                      type="button"
                      className="profile-avatar-preview-button"
                      onClick={() => setShowImageViewer(true)}
                      aria-label={t.profile.viewPhoto}
                    >
                      <img src={userProfile.imageUrl} alt="" className="profile-avatar-image" />
                    </button>
                  ) : (
                    <div className="profile-avatar-initials" aria-hidden="true">
                      {profileInitials}
                    </div>
                  )}
                  {compressingImage && (
                    <LoadingIndicator
                      message={t.profile.imageProcessing}
                      variant="compact"
                      className="profile-avatar-loading"
                    />
                  )}
                  {hasVisibleProfileImage && !compressingImage && (
                    <button
                      className="profile-btn-remove-image"
                      onClick={handleRemoveImage}
                      type="button"
                      aria-label={t.profile.removeImage}
                    >
                      <X size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
              <div className="profile-identity-copy">
                <span className="profile-identity-eyebrow">{t.profile.profilePicture}</span>
                <h2>{[firstName, lastName].filter(Boolean).join(' ') || email}</h2>
                <p>{email}</p>
                <button
                  className="profile-btn-add-photo"
                  onClick={handleAddPhotoClick}
                  type="button"
                >
                  <Pencil size={17} strokeWidth={2} aria-hidden="true" />
                  <span>{hasVisibleProfileImage ? t.profile.changeImage : t.profile.addPhoto}</span>
                </button>
              </div>
            </section>

            <div className="profile-form">
            <section className="profile-form-section" aria-labelledby="profile-personal-details">
              <header className="profile-section-heading">
                <span className="profile-section-icon"><UserRound size={19} aria-hidden="true" /></span>
                <div className="profile-section-heading-copy">
                  <h2 id="profile-personal-details">{t.profile.personalDetails}</h2>
                  <p>{t.profile.personalDetailsDescription}</p>
                </div>
              </header>
              <div className="profile-form-grid">
            <div className="profile-form-group">
              <div className="profile-description-header">
                <label className="profile-form-label" htmlFor="profile-first-name">{t.profile.firstName}</label>
                <span className="profile-character-count">{firstName.length}/50</span>
              </div>
              <input
                type="text"
                id="profile-first-name"
                className="profile-form-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value.slice(0, 50))}
              />
            </div>

            <div className="profile-form-group">
              <div className="profile-description-header">
                <label className="profile-form-label" htmlFor="profile-last-name">{t.profile.lastName}</label>
                <span className="profile-character-count">{lastName.length}/50</span>
              </div>
              <input
                type="text"
                id="profile-last-name"
                className="profile-form-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value.slice(0, 50))}
              />
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label" htmlFor="profile-phone">{t.profile.phone}</label>
              <div className="profile-phone-input-wrapper">
                <input
                  type="text"
                  id="profile-phone"
                  className={`profile-form-input ${displayedPhoneError ? 'profile-form-input-error' : ''}`}
                  value={phone}
                  onChange={(e) => {
                    setPhone(sanitizePhoneNumberInput(e.target.value));
                    setPhoneError('');
                  }}
                  placeholder={t.profile.phonePlaceholder}
                  aria-invalid={Boolean(displayedPhoneError)}
                  aria-describedby={displayedPhoneError ? 'profile-phone-error' : undefined}
                />
                {hasPhoneVerification() && (
                  <Check className="profile-phone-checkmark" size={20} strokeWidth={2.5} aria-hidden="true" />
                )}
              </div>
              {displayedPhoneError && (
                <span id="profile-phone-error" className="profile-form-error">
                  {displayedPhoneError}
                </span>
              )}
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label" htmlFor="profile-email">{t.profile.email}</label>
              <input
                type="email"
                id="profile-email"
                className="profile-form-input"
                value={email}
                disabled
                readOnly
              />
            </div>

            <div className="profile-form-group profile-form-group-wide">
              <div className="profile-description-header">
                <label className="profile-form-label" htmlFor="profile-description">{t.profile.description}</label>
                <span className="profile-character-count">
                  {description.length}/{USER_DESCRIPTION_MAX_LENGTH}
                </span>
              </div>
              <textarea
                id="profile-description"
                className="profile-form-textarea"
                value={description}
                onChange={(e) => setDescription(
                  e.target.value.slice(0, USER_DESCRIPTION_MAX_LENGTH)
                )}
                maxLength={USER_DESCRIPTION_MAX_LENGTH}
                rows={4}
                placeholder={t.profile.descriptionPlaceholder}
              />
            </div>
              </div>
            </section>

            <section className="profile-form-section" aria-labelledby="profile-producer-details">
              <header className="profile-section-heading">
                <span className="profile-section-icon"><Building2 size={19} aria-hidden="true" /></span>
                <div className="profile-section-heading-copy">
                  <h2 id="profile-producer-details">{t.profile.producerDetails}</h2>
                  <p>{t.profile.producerDetailsDescription}</p>
                </div>
              </header>
              <div className="profile-form-grid profile-form-grid-single">
            <div className="profile-form-group">
              <div className="profile-description-header">
                <label className="profile-form-label" htmlFor="profile-company-name">{t.profile.companyName}</label>
                <span className="profile-character-count">{companyName.length}/50</span>
              </div>
              <input
                type="text"
                id="profile-company-name"
                className="profile-form-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value.slice(0, 50))}
                placeholder={t.profile.companyNamePlaceholder}
              />
            </div>
              </div>
            </section>

            <section className="profile-form-section" aria-labelledby="profile-social-links">
              <header className="profile-section-heading">
                <span className="profile-section-icon"><Link2 size={19} aria-hidden="true" /></span>
                <div className="profile-section-heading-copy">
                  <h2 id="profile-social-links">{t.profile.socialLinks}</h2>
                  <p>{t.profile.socialLinksDescription}</p>
                </div>
              </header>
              <div className="profile-form-grid">
            <div className="profile-form-group">
              <label className="profile-form-label" htmlFor="profile-facebook">{t.profile.facebook}</label>
              <input
                type="url"
                id="profile-facebook"
                inputMode="url"
                autoCapitalize="none"
                spellCheck={false}
                className={`profile-form-input ${facebookError ? 'profile-form-input-error' : ''}`}
                value={facebook}
                onChange={(e) => {
                  setFacebook(e.target.value);
                  setFacebookError('');
                }}
                placeholder={t.profile.facebookPlaceholder}
              />
              {facebookError && <span className="profile-form-error">{facebookError}</span>}
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label" htmlFor="profile-instagram">{t.profile.instagram}</label>
              <input
                type="url"
                id="profile-instagram"
                inputMode="url"
                autoCapitalize="none"
                spellCheck={false}
                className={`profile-form-input ${instagramError ? 'profile-form-input-error' : ''}`}
                value={instagram}
                onChange={(e) => {
                  setInstagram(e.target.value);
                  setInstagramError('');
                }}
                placeholder={t.profile.instagramPlaceholder}
              />
              {instagramError && <span className="profile-form-error">{instagramError}</span>}
            </div>

            <div className="profile-form-group">
              <label className="profile-form-label" htmlFor="profile-tiktok">{t.profile.tiktok}</label>
              <input
                type="url"
                id="profile-tiktok"
                inputMode="url"
                autoCapitalize="none"
                spellCheck={false}
                className={`profile-form-input ${tiktokError ? 'profile-form-input-error' : ''}`}
                value={tiktok}
                onChange={(e) => {
                  setTiktok(e.target.value);
                  setTiktokError('');
                }}
                placeholder={t.profile.tiktokPlaceholder}
              />
              {tiktokError && <span className="profile-form-error">{tiktokError}</span>}
            </div>
              </div>
            </section>

            <section className="profile-form-section profile-security-section" aria-labelledby="profile-security-title">
              <header className="profile-section-heading profile-security-heading">
                <span className="profile-section-icon"><LockKeyhole size={19} aria-hidden="true" /></span>
                <div className="profile-section-heading-copy">
                  <h2 id="profile-security-title">{t.profile.security}</h2>
                  <p>{t.profile.securityDescription}</p>
                </div>
                <button className="profile-btn-change-password" onClick={handleChangePasswordClick} type="button">
                  <Pencil size={17} strokeWidth={2} aria-hidden="true" />
                  <span>{showChangePassword ? t.common.cancel : t.profile.changePassword}</span>
                </button>
              </header>

            {showChangePassword && (
              <div className="profile-password-change-section">
                <div className="profile-form-group">
                  <label className="profile-form-label" htmlFor="profile-current-password">
                    {t.profile.currentPassword}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showOldPassword ? 'text' : 'password'}
                      id="profile-current-password"
                      name="oldPassword"
                      className={`profile-form-input password-input ${passwordErrors.has('oldPassword') ? 'profile-form-input-error' : ''}`}
                      value={passwordData.oldPassword}
                      onChange={handlePasswordChange}
                      maxLength={PASSWORD_MAX_LENGTH}
                      placeholder={t.profile.currentPasswordPlaceholder}
                      aria-invalid={passwordErrors.has('oldPassword')}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      aria-label={showOldPassword ? t.register.hidePassword : t.register.showPassword}
                    >
                      {showOldPassword
                        ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                        : <Eye className="password-toggle-icon" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                <div className="profile-form-group">
                  <label className="profile-form-label" htmlFor="profile-new-password">
                    {t.profile.newPassword}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="profile-new-password"
                      name="newPassword"
                      className={`profile-form-input password-input ${passwordErrors.has('newPassword') ? 'profile-form-input-error' : ''}`}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      maxLength={PASSWORD_MAX_LENGTH}
                      placeholder={t.register.passwordPlaceholder}
                      aria-invalid={passwordErrors.has('newPassword')}
                      aria-describedby={passwordErrors.has('newPassword') && !isPasswordValid(passwordData.newPassword)
                        ? 'profile-new-password-error'
                        : undefined}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label={showNewPassword ? t.register.hidePassword : t.register.showPassword}
                    >
                      {showNewPassword
                        ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                        : <Eye className="password-toggle-icon" aria-hidden="true" />}
                    </button>
                  </div>
                  {passwordErrors.has('newPassword') && !isPasswordValid(passwordData.newPassword) && (
                    <span id="profile-new-password-error" className="profile-form-error">
                      {t.register.passwordValidation}
                    </span>
                  )}
                </div>

                <div className="profile-form-group">
                  <label className="profile-form-label" htmlFor="profile-confirm-password">
                    {t.profile.confirmNewPassword}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmNewPassword ? 'text' : 'password'}
                      id="profile-confirm-password"
                      name="confirmedNewPassword"
                      className={`profile-form-input password-input ${passwordErrors.has('confirmedNewPassword') ? 'profile-form-input-error' : ''}`}
                      value={passwordData.confirmedNewPassword}
                      onChange={handlePasswordChange}
                      maxLength={PASSWORD_MAX_LENGTH}
                      placeholder={t.profile.confirmNewPasswordPlaceholder}
                      aria-invalid={passwordErrors.has('confirmedNewPassword')}
                      aria-describedby={passwordErrors.has('confirmedNewPassword') &&
                        passwordData.confirmedNewPassword.trim() &&
                        passwordData.newPassword !== passwordData.confirmedNewPassword
                        ? 'profile-confirm-password-error'
                        : undefined}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                      aria-label={showConfirmNewPassword ? t.register.hidePassword : t.register.showPassword}
                    >
                      {showConfirmNewPassword
                        ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                        : <Eye className="password-toggle-icon" aria-hidden="true" />}
                    </button>
                  </div>
                  {passwordErrors.has('confirmedNewPassword') &&
                    passwordData.confirmedNewPassword.trim() &&
                    passwordData.newPassword !== passwordData.confirmedNewPassword && (
                      <span id="profile-confirm-password-error" className="profile-form-error">
                        {t.profile.passwordMismatch}
                      </span>
                    )}
                </div>

                <button
                  className="profile-btn-save-password"
                  onClick={handleSubmitPasswordChange}
                  disabled={changingPassword}
                  type="button"
                >
                  {changingPassword ? t.profile.changingPassword : t.profile.changePassword}
                </button>
              </div>
            )}
            </section>

            <div className="profile-save-actions">
              <div className={`profile-save-status ${hasProfileChanges ? 'has-changes' : ''}`}>
                <span>{hasProfileChanges ? <Pencil size={17} aria-hidden="true" /> : <Check size={18} aria-hidden="true" />}</span>
                <div>
                  <strong>{hasProfileChanges ? t.profile.unsavedChanges : t.profile.savedState}</strong>
                  <small>{hasProfileChanges ? t.profile.unsavedChangesHint : t.profile.savedStateHint}</small>
                </div>
              </div>
              <button
                className="profile-btn-save"
                onClick={handleSave}
                disabled={!hasProfileChanges || saving || compressingImage}
              >
                {!saving && <Save size={18} aria-hidden="true" />}
                {saving ? t.profile.saving : t.profile.saveButton}
              </button>
            </div>

            <section className="profile-delete-account-section">
              <div className="profile-delete-account-copy">
                <span className="profile-danger-label"><Trash2 size={15} aria-hidden="true" />{t.profile.dangerZone}</span>
                <h2 className="profile-delete-account-title">{t.profile.deleteAccountTitle} <em>{t.profile.deleteAccountTitleCompany}</em></h2>
                <p className="profile-delete-account-description">{t.profile.deleteAccountDescription}</p>
              </div>
              <button className="profile-btn-delete" onClick={handleDeleteAccountClick}>
                <Trash2 size={17} aria-hidden="true" />
                {t.profile.deleteAccountButton}
              </button>
            </section>
          </div>

          </div>
        </main>
      </div>

      {showDeleteAccountModal && (
        <ConfirmModal
          title={t.profile.deleteAccountModalTitle}
          message={t.profile.deleteAccountModalMessage}
          confirmText={t.profile.deleteAccountConfirm}
          cancelText={t.common.cancel}
          destructive
          isConfirming={deletingAccount}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={handleCancelDelete}
        />
      )}

      {showImageViewer && (imagePreview || (userProfile?.imageUrl && !shouldDeletePicture)) && (
        <ImageViewer
          images={[imagePreview || userProfile?.imageUrl || '']}
          initialIndex={0}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default Profile;
